/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import {
    Conversation,
    ConversationParticipant,
    ConversationStreamEvent,
    ConversationUserReference,
    User,
} from '@centecomm/portal-service';
import { useConversations, useCurrentUser } from '@centecomm/portal-service-react';
import ConversationParticipants from 'Features/Conversations/ConversationDetail/ConversationParticipants';
import { ArrowRightCircle, Close, Gear, IconColors, User as UserIcon } from 'Icons';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import TextareaAutosize from 'react-textarea-autosize';
import { FlexWrapper } from 'Shared/FlexWrapper';
import { useControlledForm } from 'Shared/FormFields';
import { LoadingContent } from 'Shared/PageElements';
import { conversationsActions, conversationsSelectors } from 'State';
import { ConversationsActionTypes } from 'State/Conversations/actions';
import { addErrorMessage, addSuccessMessage } from 'State/GlobalMessages/actionCreators';
import DynamicTranslatedContent from 'Core/i18n/Components/DynamicTranslatedContent';
import ConversationAdminPanel from '../ConversationAdminPanel/ConversationAdminPanel';
import ConversationEventList from '../ConversationEventList/ConversationEventList';
import { ConversationUsersPanel } from '../ConversationUsersPanel';
import styles from './conversation-detail.module.css';

export enum ConversationViewStates {
    ADD_USER,
    SHOW_USERS,
    DELETE_USER,
}

interface ConversationProps {
    conversationId: number;
}

function ConversationDetail({ conversationId }: ConversationProps): React.ReactElement<ConversationProps> {
    const { t } = useTranslation(['conversationsFeature']);
    const dispatch = useDispatch();

    const {
        getConversation,
        getConversationEvents,
        removeUserFromConversation,
        getConversationParticipants,
        sendConversationMessage,
    } = useConversations();

    const { isTenant, currentUser, currentUserId } = useCurrentUser();
    const userId: number = currentUserId as number;

    const eventsLoadedForConversation: boolean = useSelector(
        conversationsSelectors.eventsLoadedForConversation(conversationId),
    );

    const conversationParticipants: ConversationParticipant[] = useSelector(
        conversationsSelectors.participantsForConversation(conversationId),
    );

    const activeConversationParticipants: ConversationParticipant[] = useSelector(
        conversationsSelectors.activeParticipantsForConversation(conversationId),
    );

    const participantsLoadedForConversations: boolean = useSelector(
        conversationsSelectors.participantsLoadedForConversation(conversationId),
    );

    const conversationIsLoaded: boolean = useSelector(conversationsSelectors.conversationIsLoaded(conversationId));
    const conversationIsActive: boolean = useSelector(conversationsSelectors.conversationIsActive(conversationId));
    const conversationTopic: string = useSelector(conversationsSelectors.conversationTopic(conversationId));

    const [disableSend, setDisableSend] = React.useState<boolean>(false);
    const [showAdminPanel, toggleAdminPanel] = React.useState<boolean>(false);
    const [userIsActive, setUserIsActive] = React.useState<boolean>();
    const [showParticipants, setShowParticipants] = React.useState<boolean>(false);
    const [currentViewState, setCurrentViewState] = React.useState<ConversationViewStates>(
        ConversationViewStates.SHOW_USERS,
    );
    const [reloadParticipants, setReloadParticipants] = React.useState(false);

    const [inputValue, handler, forceUpdate] = useControlledForm();

    useEffect(() => {
        if (participantsLoadedForConversations && !!userId) {
            const current: ConversationParticipant =
                conversationParticipants.find((c: ConversationParticipant) => c.userId === userId) ||
                ({} as ConversationParticipant);

            const { isActive = false } = current;

            if (userIsActive !== isActive) setUserIsActive(isActive);
        }
    }, [userId, conversationParticipants.length, participantsLoadedForConversations]);

    useEffect(() => {
        getConversation(conversationId).then((c: Conversation) => {
            dispatch({
                type: conversationsActions.ADD_CONVERSATION,
                payload: c,
            } as ConversationsActionTypes);
        });
    }, [conversationIsLoaded]);

    useEffect(() => {
        if (!eventsLoadedForConversation) {
            getConversationEvents(conversationId).then((events: ConversationStreamEvent[]) => {
                dispatch({
                    type: conversationsActions.SET_CONVERSATION_EVENTS,
                    payload: {
                        conversationId,
                        events,
                    },
                } as ConversationsActionTypes);
            });
        }
    }, [eventsLoadedForConversation]);

    useEffect(() => {
        if (!participantsLoadedForConversations || reloadParticipants) {
            getConversationParticipants(conversationId).then((participants: ConversationParticipant[]) => {
                dispatch({
                    type: conversationsActions.SET_CONVERSTION_PARTICIPANTS,
                    payload: {
                        conversationId,
                        participants,
                    },
                } as ConversationsActionTypes);
            });

            setReloadParticipants(false);
        }
    }, [participantsLoadedForConversations, reloadParticipants]);

    const send = () => {
        if (disableSend || !inputValue.length) return;

        setDisableSend(true);

        sendConversationMessage(conversationId, inputValue)
            .then(() => {
                forceUpdate('');
                setDisableSend(false);
            })
            .catch(() => {
                dispatch(addErrorMessage(t('conversationsFeature:conversationDetail.errors.messageNotSent')));
            });
    };

    const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement | HTMLDivElement>) => {
        if (e.key === 'Enter') send();
    };

    const handleShowParticipants = () => {
        setShowParticipants(!showParticipants);
    };

    const removeUserHandler = (removableUser: ConversationUserReference) => {
        const { userId: removableUserId, userName } = removableUser;

        removeUserFromConversation(conversationId, removableUserId)
            .then(() => {
                dispatch(
                    addSuccessMessage(
                        t('conversationsFeature:conversationDetail.participants.modal.actions.remove.success', {
                            username: userName,
                        }),
                    ),
                );
                setReloadParticipants(true);
            })
            .catch(() => {
                dispatch(
                    addErrorMessage(
                        t('conversationsFeature:conversationDetail.participants.modal.actions.remove.errors.withUsername', {
                            username: userName,
                        }),
                    ),
                );
            });
    };

    return (
        <div className={styles.Conversation}>
            <div className={styles.ConversationContent}>
                {conversationIsLoaded && eventsLoadedForConversation && typeof userIsActive === 'boolean' ? (
                    <>
                        <div className={styles.ConversationTitleBar}>
                            <p>
                                <DynamicTranslatedContent
                                    content={conversationTopic}
                                    showLoadingState
                                    extendedOptions={{
                                        bypassMainAppLanguage: true,
                                    }}
                                >
                                    {({ translatedContent }) => <>{translatedContent}</>}
                                </DynamicTranslatedContent>
                            </p>
                            {conversationIsActive && userIsActive && (
                                <FlexWrapper align="start" justify="end" gap="1rem">
                                    <span
                                        onClick={handleShowParticipants}
                                        style={{
                                            color: showParticipants ? IconColors.WarningRed : IconColors.Action,
                                            fontSize: '0.75rem',
                                        }}
                                    >
                                        <UserIcon aspect="1.5rem" />
                                        {activeConversationParticipants.length}
                                    </span>

                                    <div
                                        data-qa="toggle-admin-panel-button"
                                        onClick={() => {
                                            toggleAdminPanel(!showAdminPanel);
                                        }}
                                    >
                                        {showAdminPanel ? (
                                            <Close aspect="1.5rem" color={IconColors.Action} />
                                        ) : (
                                            <Gear color={IconColors.Action} />
                                        )}
                                    </div>
                                </FlexWrapper>
                            )}
                        </div>
                        {showAdminPanel ? (
                            <ConversationAdminPanel
                                conversationId={conversationId}
                                closePanel={() => {
                                    toggleAdminPanel(false);
                                }}
                            />
                        ) : (
                            <>
                                {showParticipants && currentViewState === ConversationViewStates.SHOW_USERS && (
                                    <ConversationParticipants
                                        participants={activeConversationParticipants}
                                        currentUser={currentUser as User}
                                        onAddUserHandler={() => setCurrentViewState(ConversationViewStates.ADD_USER)}
                                        onRemoveUserHandler={removeUserHandler}
                                    />
                                )}
                                {showParticipants && currentViewState === ConversationViewStates.ADD_USER && (
                                    <ConversationUsersPanel
                                        conversationId={conversationId}
                                        onGoBackHandler={() => setCurrentViewState(ConversationViewStates.SHOW_USERS)}
                                        onAddNewUser={() => setReloadParticipants(true)}
                                    />
                                )}
                                {isTenant && (
                                    <p key="event-list-tenant-disclaimer" className={styles.DisclaimerBlock}>
                                        {t('conversationsFeature:conversationDetail.disclaimer')}
                                    </p>
                                )}
                                {!showParticipants && (
                                    <>
                                        <ConversationEventList conversationId={conversationId} hasDisclaimer={isTenant} />
                                        {conversationIsActive && userIsActive && (
                                            <div className={styles.MessageInput}>
                                                <TextareaAutosize
                                                    style={{ resize: 'none', width: '100%' }}
                                                    className={styles.Input}
                                                    maxRows={3}
                                                    id="conversation-input"
                                                    name="Conversation Input"
                                                    value={inputValue}
                                                    onChange={handler}
                                                    disabled={!conversationIsActive}
                                                    onKeyPress={handleKeyPress}
                                                    autoComplete="off"
                                                />
                                                <div
                                                    className={styles.SendMessage}
                                                    onKeyPress={handleKeyPress}
                                                    onClick={() => {
                                                        if (conversationIsActive) send();
                                                    }}
                                                >
                                                    <ArrowRightCircle />
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </>
                        )}
                    </>
                ) : (
                    <div className={styles.LoadingWrapper}>
                        <LoadingContent />
                    </div>
                )}
            </div>
        </div>
    );
}

export default ConversationDetail;
